/**
 * Nasadkali kolonna gidravlikasi (soddalashtirilgan): to'lib ketish va bosim yo'qotish.
 *
 * MANBA: Kasatkin, gl. XVI ("Гидравлическое сопротивление насадочных колонн",
 *        "Предельная (захлёбывания) скорость газа") — Sherwood–Leva–Eckert umumlashtirilgan
 *        korrelyatsiyasining PAJ ko'rinishi; Pavlov, Romankov, Noskov, gl. 6 (formula 6.16–6.20);
 *        Perry's 8th ed., Sec. 14 (GPDC).
 *
 * TENGLAMALAR:
 *   To'lib ketish (Kasatkin/PRN, μ_L mPa·s, L va G — massa sarflari):
 *     lg[ u_f²·a·ρ_G·μ_L^0.16 / (g·ε³·ρ_L) ] = A − 1.75·(L/G)^0.25·(ρ_G/ρ_L)^0.125
 *     Fp = a/ε³  →  u_f² = g·ρ_L·10^(RHS) / (Fp·ρ_G·μ_L^0.16)
 *   Gaz tezligi (to'liq kesimga):  u = G_mass/(ρ_G·S),  S = πD²/4
 *   Quruq nasadka (Kasatkin):
 *     d_e = 4ε/a,  u_0 = u/ε,  Re = 4·u·ρ_G/(a·μ_G)
 *     λ = 140/Re (Re ≤ 40),  λ = 16/Re^0.2 (Re > 40)
 *     ΔP_dry/Z = λ·(1/d_e)·ρ_G·u_0²/2
 *   Ho'llangan nasadka: ΔP = ΔP_dry·10^(b·U),  U = L_mass/(ρ_L·S), m/s
 *
 * ASSUMPTION:
 *   1. Gaz — tashuvchi komponent xossalari (yutiladigan komponent ulushi kichik).
 *   2. Suyuqlik — toza erituvchi xossalari.
 *   3. Nasadka tartibsiz to'kilgan halqalar (A = 0.022, b = 173) — standart; boshqa
 *      nasadkalar uchun koeffitsientlar param orqali.
 *   4. Devor effekti, suyuqlik taqsimlanishi (maldistribution) hisobga olinmagan.
 *   5. Ho'llangan bosim yo'qotish formulasi to'lib ketish nuqtasidan pastda (u/u_f < 0.8) amal qiladi.
 */
import { gasDensity, getSubstance, liquidDensity, viscosity } from '../../properties';
import type { WarningSink } from '../../types';
import type { AbsorberParams } from './types';

export const DEFAULT_FLOODING_A = 0.022;
export const DEFAULT_IRRIGATION_B = 173;
const G_ACC = 9.81;

export interface PackedHydraulics {
  /** kg/m³ */
  gasDensity: number;
  liquidDensity: number;
  /** m/s */
  gasVelocity: number;
  floodingVelocity: number;
  floodingFraction: number;
  /** Pa/m */
  pressureDropPerMeter: number;
  /** 1/m */
  packingFactor: number;
  Re: number;
}

export function columnArea(p: AbsorberParams): number {
  return (Math.PI * p.diameter * p.diameter) / 4;
}

export function packingFactorOf(p: AbsorberParams): number {
  return p.packingFactor ?? p.specificArea / Math.pow(p.voidFraction, 3);
}

/** Kasatkin/PRN to'lib ketish tezligi, m/s (Gm, Lm — massa sarflari kg/s) */
export function floodingVelocity(
  Fp: number,
  A: number,
  rhoG: number,
  rhoL: number,
  muL: number,
  Lm: number,
  Gm: number,
): number {
  if (!(Gm > 0)) return Infinity;
  const muLmPa = muL * 1000;
  const rhs = A - 1.75 * Math.pow(Lm / Gm, 0.25) * Math.pow(rhoG / rhoL, 0.125);
  const uf2 = (G_ACC * rhoL * Math.pow(10, rhs)) / (Fp * rhoG * Math.pow(muLmPa, 0.16));
  return Math.sqrt(uf2);
}

/** Quruq nasadka bosim yo'qotishi, Pa/m */
export function dryPressureDropPerMeter(
  p: AbsorberParams,
  u: number,
  rhoG: number,
  muG: number,
): { dp: number; Re: number } {
  const a = p.specificArea;
  const eps = p.voidFraction;
  if (!(u > 0)) return { dp: 0, Re: 0 };
  const Re = (4 * u * rhoG) / (a * muG);
  const lambda = Re <= 40 ? 140 / Re : 16 / Math.pow(Re, 0.2);
  const de = (4 * eps) / a;
  const u0 = u / eps;
  return { dp: (lambda / de) * ((rhoG * u0 * u0) / 2), Re };
}

export function packedHydraulics(
  p: AbsorberParams,
  args: { G: number; L: number; T: number; P: number },
  warnings?: WarningSink,
): PackedHydraulics {
  const MG = getSubstance(p.carrierGas).molarMass;
  const ML = getSubstance(p.solvent).molarMass;
  const rhoG = gasDensity(p.carrierGas, args.T, args.P);
  const rhoL = liquidDensity(p.solvent, args.T, warnings);
  const muG = viscosity(p.carrierGas, 'gas', args.T, warnings);
  const muL = viscosity(p.solvent, 'liquid', args.T, warnings);
  const S = columnArea(p);
  const Gm = Math.max(args.G, 0) * MG;
  const Lm = Math.max(args.L, 0) * ML;
  const u = Gm / (rhoG * S);
  const Fp = packingFactorOf(p);
  const uf = floodingVelocity(
    Fp,
    p.floodingCoefficientA ?? DEFAULT_FLOODING_A,
    rhoG,
    rhoL,
    muL,
    Lm,
    Gm,
  );
  const dry = dryPressureDropPerMeter(p, u, rhoG, muG);
  const U = Lm / (rhoL * S);
  const b = p.irrigationCoefficientB ?? DEFAULT_IRRIGATION_B;
  return {
    gasDensity: rhoG,
    liquidDensity: rhoL,
    gasVelocity: u,
    floodingVelocity: uf,
    floodingFraction: Number.isFinite(uf) && uf > 0 ? u / uf : 0,
    pressureDropPerMeter: dry.dp * Math.pow(10, b * U),
    packingFactor: Fp,
    Re: dry.Re,
  };
}
